import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, CheckCircle2 } from "lucide-react";
import { authReset, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) return setError("Password must be at least 8 characters.");
    if (pw !== pw2) return setError("Passwords do not match.");
    if (!token) return setError("Missing reset token. Use the link from your email.");
    setLoading(true);
    try {
      await authReset(token, pw);
      setDone(true);
      toast.success("Password updated. Please sign in.");
    } catch (err) {
      setError(formatApiError(err, "Could not reset password."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-page" className="max-w-md mx-auto px-6 py-20">
      <div className="cc-overline inline-flex items-center gap-2">
        <Lock size={11} /> New password
      </div>
      <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mt-3 mb-2">
        Choose a new <span className="text-[#00ff88]">password</span>.
      </h1>

      {done ? (
        <div data-testid="reset-success" className="cc-surface rounded-2xl p-7 text-center mt-6">
          <CheckCircle2 className="text-[#00ff88] mx-auto mb-3" size={36} />
          <div className="text-base text-white mb-1">Password updated.</div>
          <div className="text-xs text-[#a0a0b0]">Log in with your new password.</div>
          <Link to="/login" data-testid="reset-login-btn" className="cc-btn-primary inline-flex mt-4 px-5 py-2 rounded-lg text-sm">
            Go to login
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="cc-surface rounded-2xl p-7 space-y-5 mt-6">
          <Field label="New password">
            <input
              data-testid="reset-password"
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              minLength={6}
              className="w-full bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2.5 text-sm text-white cc-focus-ring"
              required
            />
          </Field>
          <Field label="Confirm password">
            <input
              data-testid="reset-password-confirm"
              type="password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className="w-full bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2.5 text-sm text-white cc-focus-ring"
              required
            />
          </Field>

          {error && (
            <div data-testid="reset-error" className="text-xs text-[#ff8888] bg-[#ff4444]/5 border border-[#ff4444]/30 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          <button
            data-testid="reset-submit"
            disabled={loading}
            className="cc-btn-primary w-full rounded-lg px-6 py-2.5 text-sm disabled:opacity-50"
          >
            {loading ? "Updating…" : "Update password"}
          </button>

          <div className="text-[11px] text-[#606070] text-center">
            <Link to="/login" className="hover:text-white">Back to sign in</Link>
          </div>
        </form>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <div className="cc-overline mb-1.5">{label}</div>
    {children}
  </div>
);
